import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminPageTitle,
  AdminHeaderAction,
  AdminDetailFieldLabel,
  AdminSupportingText,
  AdminFormFieldError,
  adminFormDetailInputClasses,
  adminUploadFieldClasses,
  DASHBOARD_DETAIL_FIELD_WIDTH,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import { matchesUrlName } from '../../../utils/slugify';
import { buildSampleTradeCategories } from './tradeCategoriesDummyData';
import {
  DEFAULT_ACTOR,
  FIELD_ERRORS,
  FORM_MODE,
  ID_PLACEHOLDER,
  SAMPLE_VALUES,
  SAVE_MESSAGES,
  TRADE_CATEGORY_ROUTE,
  buildConfirmedTradeCategorySave,
  cloneTradeCategoryRecord,
  collectSaveValidationErrors,
  emptySubcategoryBlock,
  emptyTradeCategoryRecord,
  formatTradeCategoryTableDate,
  getTradeSubcategories,
  isIconFileSupported,
  resolvePublicFacingName,
} from '../../../data/tradeCategoryWorkflow';
import { resolveTradeCategoryIconSrc } from '../../../data/tradeCategoryIconMap';

const ReadOnlyField = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <input
      type="text"
      value={value || '—'}
      readOnly
      className={`${adminFormDetailInputClasses(true)}${mono ? ' font-mono' : ''}`}
    />
  </div>
);

const CategoryIconUpload = ({
  label = 'Trade Category Icon',
  value,
  fileName,
  error,
  onChange,
  exampleHint = SAMPLE_VALUES.tradeCategoryIcon,
  required = true,
}) => {
  const inputRef = useRef(null);
  const hasFile = Boolean(fileName || value);
  const previewSrc = resolveTradeCategoryIconSrc(value, fileName);

  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        {label} {required ? <span className="text-alertred">*</span> : null}
      </AdminDetailFieldLabel>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={`${adminUploadFieldClasses()} ${DASHBOARD_DETAIL_FIELD_WIDTH} !bg-white !min-h-[calc(var(--dashboard-field-medium-height)*3)] !h-[calc(var(--dashboard-field-medium-height)*3)] !py-2 hover:border-primaryblue flex flex-col items-center justify-center gap-1 text-center cursor-pointer ${
          error ? '!border-alertred' : ''
        }`}
      >
        {previewSrc ? (
          <img src={previewSrc} alt="" className="w-8 h-8 object-contain" />
        ) : (
          <i className="fi fi-rs-picture text-base text-mediumgray" aria-hidden />
        )}
        <span className={`text-sm ${hasFile ? 'text-offblack' : 'text-primaryblue'}`}>
          {hasFile ? fileName || 'Icon uploaded' : 'Upload icon'}
        </span>
        <AdminSupportingText className="mb-0">
          {`PNG, JPG or SVG — e.g. ${exampleHint}`}
        </AdminSupportingText>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          if (!isIconFileSupported(file)) {
            onChange({
              iconUrl: '',
              iconFileName: '',
              iconError: 'The selected icon file is not supported.',
            });
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            onChange({ iconUrl: reader.result, iconFileName: file.name, iconError: '' });
          };
          reader.readAsDataURL(file);
        }}
      />
      {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
    </div>
  );
};

const SubcategoryBlock = ({
  index,
  sub,
  fieldErrors,
  canRemove,
  isAdd,
  onChange,
  onRemove,
}) => {
  const rowKey = sub.id || sub.localKey || `idx-${index}`;
  const nameError = fieldErrors[`tradeSub-${rowKey}`];
  const iconError = fieldErrors[`tradeSub-${rowKey}-publicIcon`];
  const showPublicFields = Boolean(sub.displayAsPublicFacing);
  const publicName = resolvePublicFacingName(sub);

  return (
    <div className="rounded-lg border border-lightgray p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <AdminSupportingText className="mb-0 font-semibold !text-offblack">
          Subcategory {index + 1}
        </AdminSupportingText>
        {canRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="shrink-0 inline-flex items-center justify-center leading-none text-offblack hover:text-alertred"
            aria-label={`Remove Subcategory ${index + 1}`}
          >
            <i className="fi fi-rs-trash text-sm" aria-hidden />
          </button>
        ) : null}
      </div>

      {!isAdd ? (
        <ReadOnlyField label="Subcategory ID" value={sub.id || ID_PLACEHOLDER} mono />
      ) : null}

      <div className="space-y-1 w-full min-w-0">
        <input
          type="text"
          placeholder={
            index === 0 ? SAMPLE_VALUES.subcategoryName : SAMPLE_VALUES.publicFacingSubcategoryName
          }
          value={sub.name || ''}
          onChange={(e) => onChange({ name: e.target.value })}
          className={adminFormDetailInputClasses(false, Boolean(nameError))}
        />
        {nameError ? <AdminFormFieldError>{nameError}</AdminFormFieldError> : null}
      </div>

      <div className="flex items-center justify-between gap-3">
        <AdminSupportingText className="mb-0">Make Public-Facing</AdminSupportingText>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            role="switch"
            aria-checked={Boolean(sub.displayAsPublicFacing)}
            checked={Boolean(sub.displayAsPublicFacing)}
            onChange={(e) => onChange({ displayAsPublicFacing: e.target.checked })}
            className="sr-only peer"
          />
          <div
            className="relative w-9 h-5 bg-lightgray rounded-full pointer-events-none peer-checked:bg-primaryblue after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"
            aria-hidden
          />
        </label>
      </div>

      {showPublicFields ? (
        <div className="space-y-3">
          <ReadOnlyField label="Public-Facing Name" value={publicName || '—'} />
          <CategoryIconUpload
            label="Public-Facing Icon"
            value={sub.publicFacingIconUrl}
            fileName={sub.publicFacingIconFileName}
            exampleHint={SAMPLE_VALUES.publicFacingIcon}
            error={iconError}
            onChange={({ iconUrl, iconFileName, iconError: err }) => {
              onChange({
                publicFacingIconUrl: iconUrl,
                publicFacingIconFileName: iconFileName,
                _publicIconError: err || '',
              });
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

function resolveFormMode(pathname, categoryName) {
  if (pathname.endsWith('/add') || !categoryName) return FORM_MODE.ADD;
  return FORM_MODE.EDIT;
}

const TradeCategoryDetail = () => {
  const navigate = useNavigate();
  const { categoryName } = useParams();
  const location = useLocation();

  const mode = resolveFormMode(location.pathname, categoryName);
  const isAdd = mode === FORM_MODE.ADD;
  const isEdit = mode === FORM_MODE.EDIT;

  const allCategories = useMemo(
    () => location.state?.tradeCategories ?? buildSampleTradeCategories(),
    [location.state?.tradeCategories],
  );

  const listRecord = useMemo(() => {
    if (isAdd) return null;
    const fromState = location.state?.category;
    if (fromState && matchesUrlName(fromState.name, categoryName)) return fromState;
    return allCategories.find((c) => matchesUrlName(c.name, categoryName)) || null;
  }, [isAdd, categoryName, location.state?.category, allCategories]);

  const [formData, setFormData] = useState(emptyTradeCategoryRecord);
  const [baseline, setBaseline] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    if (isAdd) {
      setFormData(emptyTradeCategoryRecord());
      setBaseline(null);
      setFieldErrors({});
      setSaveMessage('');
      return;
    }
    if (listRecord) {
      const cloned = cloneTradeCategoryRecord(listRecord);
      setFormData(cloned);
      setBaseline(cloneTradeCategoryRecord(listRecord));
      setFieldErrors({});
      setSaveMessage('');
    }
  }, [isAdd, listRecord]);

  const pageHeading = isAdd ? 'Add Trade Category' : 'Edit Trade Category';

  const persistCategory = (updated, flashMessage) => {
    const nextList = isAdd
      ? [...allCategories, updated]
      : allCategories.map((c) => (c.id === updated.id ? updated : c));
    navigate(TRADE_CATEGORY_ROUTE, {
      state: { tradeCategories: nextList, flashMessage },
    });
  };

  const updateSubcategory = (rowKey, patch) => {
    const { _publicIconError, ...rest } = patch;
    setFormData((prev) => ({
      ...prev,
      tradeSubcategories: getTradeSubcategories(prev).map((s) =>
        (s.id || s.localKey) === rowKey ? { ...s, ...rest } : s,
      ),
    }));
    if (_publicIconError !== undefined) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        const errKey = `tradeSub-${rowKey}-publicIcon`;
        if (_publicIconError) next[errKey] = _publicIconError;
        else delete next[errKey];
        return next;
      });
    }
  };

  const addSubcategoryBlock = () => {
    setFormData((prev) => ({
      ...prev,
      tradeSubcategories: [...getTradeSubcategories(prev), emptySubcategoryBlock()],
    }));
  };

  const removeSubcategoryBlock = (rowKey) => {
    setFormData((prev) => {
      const blocks = getTradeSubcategories(prev);
      const target = blocks.find((s) => (s.id || s.localKey) === rowKey);
      if (!target || target.id) return prev;
      if (blocks.length <= 1) return prev;
      return {
        ...prev,
        tradeSubcategories: blocks.filter((s) => (s.id || s.localKey) !== rowKey),
      };
    });
    setFieldErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`tradeSub-${rowKey}`)) delete next[key];
      });
      return next;
    });
  };

  const handleSave = () => {
    const payload = cloneTradeCategoryRecord(formData);
    const latestRecord = isEdit
      ? allCategories.find((c) => c.id === (baseline?.id || listRecord?.id)) || listRecord
      : null;
    const { errors } = collectSaveValidationErrors(payload, allCategories, {
      baseline: isEdit ? baseline : null,
      latestRecord,
    });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSaveMessage(errors.concurrent || SAVE_MESSAGES.failure);
      return;
    }
    setFieldErrors({});
    setSaveMessage('');
    const saved = buildConfirmedTradeCategorySave(formData, allCategories, {
      isAdd,
      actor: DEFAULT_ACTOR,
      baseline: isEdit ? baseline : null,
    });
    persistCategory(saved, isAdd ? SAVE_MESSAGES.created : SAVE_MESSAGES.updated);
  };

  const nameInputClass = adminFormDetailInputClasses(false, Boolean(fieldErrors.name));
  const subcategoryBlocks = getTradeSubcategories(formData);

  if (!isAdd && !listRecord) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Edit Trade Category</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray">Trade Category not found.</AdminSupportingText>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-[25rem] mx-auto w-full min-w-0">
        <div className="mb-6">
          <AdminPageTitle className="!mb-0">{pageHeading}</AdminPageTitle>
        </div>

        {saveMessage ? (
          <div className="mb-6">
            <AdminFormFieldError className="mb-0">{saveMessage}</AdminFormFieldError>
          </div>
        ) : null}

        {fieldErrors.concurrent ? (
          <div className="mb-6">
            <AdminFormFieldError className="mb-0">{FIELD_ERRORS.concurrent}</AdminFormFieldError>
          </div>
        ) : null}

        <div className="p-4 space-y-6 min-w-0">
          {isEdit ? <ReadOnlyField label="Trade ID" value={formData.id} mono /> : null}

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Trade Category Name <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <input
              type="text"
              placeholder={SAMPLE_VALUES.tradeCategoryName}
              value={formData.name || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              className={nameInputClass}
            />
            {fieldErrors.name ? (
              <AdminFormFieldError>{fieldErrors.name}</AdminFormFieldError>
            ) : null}
          </div>

          <CategoryIconUpload
            label="Trade Category Icon"
            value={formData.iconUrl}
            fileName={formData.iconFileName}
            exampleHint={SAMPLE_VALUES.tradeCategoryIcon}
            error={fieldErrors.icon}
            onChange={({ iconUrl, iconFileName, iconError }) => {
              setFormData((prev) => ({ ...prev, iconUrl, iconFileName }));
              if (iconError) {
                setFieldErrors((prev) => ({ ...prev, icon: iconError }));
              } else {
                setFieldErrors((prev) => {
                  const next = { ...prev };
                  delete next.icon;
                  return next;
                });
              }
            }}
          />

          {isEdit ? (
            <>
              <ReadOnlyField
                label="Created On"
                value={formatTradeCategoryTableDate(formData.createdOn)}
              />
              <ReadOnlyField
                label="Last Updated On"
                value={formatTradeCategoryTableDate(formData.lastUpdatedOn)}
              />
            </>
          ) : null}

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <AdminDetailFieldLabel className="!mb-0">Subcategories</AdminDetailFieldLabel>
              <AdminSupportingText className="mb-0">
                {subcategoryBlocks.length} added
              </AdminSupportingText>
            </div>
            {fieldErrors.tradeSubcategories ? (
              <AdminFormFieldError>{fieldErrors.tradeSubcategories}</AdminFormFieldError>
            ) : null}
            <div className="space-y-3">
              {subcategoryBlocks.map((sub, index) => {
                const rowKey = sub.id || sub.localKey || `idx-${index}`;
                return (
                  <SubcategoryBlock
                    key={rowKey}
                    index={index}
                    sub={sub}
                    fieldErrors={fieldErrors}
                    canRemove={!sub.id && subcategoryBlocks.length > 1}
                    isAdd={isAdd}
                    onChange={(patch) => updateSubcategory(rowKey, patch)}
                    onRemove={() => removeSubcategoryBlock(rowKey)}
                  />
                );
              })}
            </div>
            <button
              type="button"
              onClick={addSubcategoryBlock}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primaryblue hover:text-brightblue"
            >
              <i className="fi fi-rs-plus text-xs" aria-hidden />
              Add Subcategory
            </button>
          </div>

          <div className="flex items-center justify-end gap-3">
            <AdminHeaderAction size="lg" type="button" onClick={handleSave}>
              Save
            </AdminHeaderAction>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default TradeCategoryDetail;
